import * as React from 'react';
import { cn } from '@noor/lib/cn';

export interface DonutSegment {
  id: string;
  label: string;
  percentage: number;
  count?: number | undefined;
  color: string;
}

export interface DonutChartProps {
  segments: DonutSegment[];
  size?: number;
  strokeWidth?: number;
  showLegend?: boolean;
  className?: string;
  formatTooltip?: (segment: DonutSegment) => string;
}

interface SliceData extends DonutSegment {
  path: string;
  xMid: number;
  yMid: number;
  angleSpan: number;
}

function buildDonutSlices(
  segments: DonutSegment[],
  size: number,
  strokeWidth: number,
): SliceData[] {
  const cx = size / 2;
  const cy = size / 2;
  const outerRadius = size / 2 - 4;
  const innerRadius = outerRadius - strokeWidth;
  const midRadius = (innerRadius + outerRadius) / 2;

  let currentAngle = 0;
  return segments.map((seg) => {
    const angleSpan = (seg.percentage / 100) * 2 * Math.PI;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angleSpan;
    currentAngle = endAngle;

    // SVG coordinates
    const xOutStart = cx + outerRadius * Math.sin(startAngle);
    const yOutStart = cy - outerRadius * Math.cos(startAngle);
    const xOutEnd = cx + outerRadius * Math.sin(endAngle);
    const yOutEnd = cy - outerRadius * Math.cos(endAngle);

    const xInStart = cx + innerRadius * Math.sin(startAngle);
    const yInStart = cy - innerRadius * Math.cos(startAngle);
    const xInEnd = cx + innerRadius * Math.sin(endAngle);
    const yInEnd = cy - innerRadius * Math.cos(endAngle);

    const midAngle = startAngle + angleSpan / 2;
    const xMid = cx + midRadius * Math.sin(midAngle);
    const yMid = cy - midRadius * Math.cos(midAngle);

    const largeArcFlag = angleSpan > Math.PI ? 1 : 0;

    const path = [
      `M ${xOutStart.toFixed(2)} ${yOutStart.toFixed(2)}`,
      `A ${outerRadius.toFixed(2)} ${outerRadius.toFixed(2)} 0 ${largeArcFlag} 1 ${xOutEnd.toFixed(2)} ${yOutEnd.toFixed(2)}`,
      `L ${xInEnd.toFixed(2)} ${yInEnd.toFixed(2)}`,
      `A ${innerRadius.toFixed(2)} ${innerRadius.toFixed(2)} 0 ${largeArcFlag} 0 ${xInStart.toFixed(2)} ${yInStart.toFixed(2)}`,
      'Z',
    ].join(' ');

    return {
      ...seg,
      path,
      xMid,
      yMid,
      angleSpan,
    };
  });
}

export function DonutChart({
  segments,
  size = 230,
  strokeWidth = 38,
  showLegend = true,
  className,
  formatTooltip = (s) =>
    `${s.label}: ${s.percentage}%${s.count !== undefined ? ` (${s.count})` : ''}`,
}: DonutChartProps) {
  const [hoveredId, setHoveredId] = React.useState<string | null>(null);

  const slices = React.useMemo(
    () => buildDonutSlices(segments, size, strokeWidth),
    [segments, size, strokeWidth],
  );

  const activeSegment = segments.find((s) => s.id === hoveredId);

  return (
    <div className={cn('flex flex-col items-center justify-between', className)}>
      <div
        className="relative flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="overflow-visible"
        >
          {slices.map((slice) => {
            const isHovered = hoveredId === slice.id;
            return (
              <g
                key={slice.id}
                className="cursor-pointer transition-transform duration-200"
                onMouseEnter={() => setHoveredId(slice.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <path
                  d={slice.path}
                  fill={slice.color}
                  stroke="#ffffff"
                  strokeWidth="5"
                  strokeLinejoin="round"
                  className={cn(
                    'transition-all duration-200',
                    isHovered ? 'brightness-110 drop-shadow-sm filter' : 'opacity-100',
                  )}
                />
                {/* Percentage label in center of slice if large enough */}
                {slice.percentage >= 6 && (
                  <text
                    x={slice.xMid}
                    y={slice.yMid}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#ffffff"
                    className="pointer-events-none text-[12px] font-bold select-none"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                  >
                    {slice.percentage}%
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Hover Tooltip Overlay */}
        {activeSegment && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <div className="rounded-full bg-popover/90 px-3 py-1 text-center shadow-xs backdrop-blur-xs">
              <span className="text-xs font-semibold text-popover-foreground">
                {formatTooltip(activeSegment)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Legend below donut */}
      {showLegend && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[14px] font-medium text-[#1f2020]">
          {segments.map((seg) => (
            <div
              key={seg.id}
              className={cn(
                'flex cursor-pointer items-center gap-1.5 transition-opacity',
                hoveredId && hoveredId !== seg.id ? 'opacity-50' : 'opacity-100',
              )}
              onMouseEnter={() => setHoveredId(seg.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: seg.color }}
              />
              <span className="whitespace-nowrap">{seg.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
